import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { usePlan } from '../context/PlanContext';

import {
  ArrowRight,
  Crown,
  Sparkle
} from '@phosphor-icons/react';
const UpgradeBanner = ({ targetPlan = 'builder', context = 'general', onUpgrade }) => {
  const navigate = useNavigate();
  const { userPlan, isFreePlan, isProPlan } = usePlan();

  // Don't show banner if user already has target plan or higher
  if (targetPlan === 'builder' && (userPlan === 'builder' || userPlan === 'pro' || userPlan === 'partner_pro' || userPlan === 'partner-pro')) {
    return null;
  }
  if (targetPlan === 'pro' && (isProPlan() || userPlan === 'partner_pro')) {
    return null;
  }
  if (targetPlan === 'partner_pro' && userPlan === 'partner_pro') {
    return null;
  }

  const planLabel = userPlan === 'basic' || userPlan === 'starter' || !userPlan ? 'Basic' : userPlan.charAt(0).toUpperCase() + userPlan.slice(1);

  const bannerConfig = {
    starter_to_builder: {
      title: `You're on the ${planLabel} plan`,
      description: 'Unlock full opportunity analysis, uploads, and AI-powered insights.',
      ctaText: 'Upgrade',
      icon: Sparkle,
      gradient: 'from-[#6366F1] to-[#EC4899]'
    },
    starter_to_pro: {
      title: `You're on the ${planLabel} plan`,
      description: 'Unlock reports, team features, and unlimited assessments.',
      ctaText: 'Upgrade',
      icon: Sparkle,
      gradient: 'from-[#6366F1] to-[#EC4899]'
    },
    pro_to_partner: {
      title: 'Consultant or agency?',
      description: 'Partner Pro unlocks client dashboards, consultant tools, and white-label reports.',
      ctaText: 'Upgrade',
      icon: Crown,
      gradient: 'from-[#EC4899] to-[#8B5CF6]'
    }
  };

  const config = targetPlan === 'partner_pro' 
    ? bannerConfig.pro_to_partner 
    : targetPlan === 'builder'
    ? bannerConfig.starter_to_builder
    : bannerConfig.starter_to_pro;

  // Contextual descriptions
  const contextualDescriptions = {
    'scanner': targetPlan === 'builder' 
      ? 'Unlock all automation opportunities, complete ROI insights, and implementation roadmap.'
      : targetPlan === 'pro'
      ? 'Get reports, team management, and unlimited assessments.'
      : 'Get advanced consultant tools to manage all client scans and provide expert guidance.',
    'processes': targetPlan === 'builder'
      ? 'Access process diagram uploads, AI-powered analysis, and advanced optimization features.'
      : targetPlan === 'pro'
      ? 'Unlock consultant dashboard to view all client processes and provide strategic recommendations.'
      : config.description,
    'general': config.description
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8 p-6 rounded-2xl glass border border-[rgba(255,255,255,0.1)] overflow-hidden relative"
    >
      {/* Background Gradient Effect */}
      <div className={`absolute inset-0 bg-gradient-to-r ${config.gradient} opacity-5`}></div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center flex-shrink-0`}>
            <config.icon size={24} className="text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-1">
              {config.title}
            </h3>
            <p className="text-[#A0AEC0] text-sm">
              {contextualDescriptions[context] || config.description}
            </p>
          </div>
        </div>

        <button
          onClick={() => onUpgrade ? onUpgrade() : navigate('/pricing')}
          data-testid="upgrade-banner-cta"
          className="flex-shrink-0 relative overflow-hidden rounded-full group"
        >
          <div className={`absolute inset-0 bg-gradient-to-r ${config.gradient}`}></div>
          <span className="relative z-10 flex items-center justify-center gap-2 px-6 py-3 text-white font-bold text-sm">
            {config.ctaText}
            <ArrowRight size={18} />
          </span>
        </button>
      </div>
    </motion.div>
  );
};

export default UpgradeBanner;
